import type { JSX } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, Select } from '@artosera/ui';
import type { Comptabilite, MoisComptable, MoisComptableAssocie } from '../../types.js';
import commun from '../VueDAigle/commun.module.css';
import { NonComptees, Section } from '../VueDAigle/composants.js';
import { useFormats } from '../VueDAigle/formats.js';
import styles from './Comptabilite.module.css';

/** La pastille d'état d'un mois : provisoire (recalculé) ou clôturé (figé). */
export function PastilleStatut({ cloture }: { cloture: boolean }): JSX.Element {
  const { t } = useTranslation('console');
  return (
    <span className={`${commun.pastille} ${cloture ? commun.pastilleVert : commun.pastilleAmbre}`}>
      {t(`comptabilite.statut.${cloture ? 'cloture' : 'provisoire'}`)}
    </span>
  );
}

function Tuile({
  titre,
  aide,
  children,
  cle = false,
  texte = false,
}: {
  titre: string;
  aide: string;
  children: string;
  cle?: boolean;
  texte?: boolean;
}): JSX.Element {
  return (
    <div className={`${commun.tuile} ${styles.tuileFluide}`}>
      <dt>{titre}</dt>
      <dd
        className={[
          commun.valeur,
          cle ? commun.valeurCle : '',
          texte ? styles.valeurTexte : styles.valeurFluide,
        ].join(' ')}
      >
        {children}
      </dd>
      <dd className={commun.aide}>{aide}</dd>
    </div>
  );
}

/**
 * Le mois en chiffres : le sélecteur de mois, puis les tuiles. La direction lit
 * le HT, la TVA, le TTC et ce qui reste à saisir ou à classer ; l'associé, le
 * HT du mois et sa part — jamais la TVA.
 */
export function MoisEnChiffres({
  vue,
  selection,
  onChoisir,
  numero,
}: {
  vue: Comptabilite;
  selection: string;
  onChoisir: (mois: string) => void;
  numero: string;
}): JSX.Element {
  const { t } = useTranslation('console');
  const f = useFormats();
  const liste = vue.mois;
  const trouve = liste.findIndex((m) => m.mois === selection);
  const position = trouve === -1 ? liste.length - 1 : trouve;
  const courant = liste[position]!;
  const cloture = courant.statut === 'cloture';

  const options = [...liste].reverse().map((m) => ({
    value: m.mois,
    label:
      m.statut === 'cloture'
        ? f.moisLong(m.mois)
        : t('comptabilite.mois.provisoireSuffixe', { mois: f.moisLong(m.mois) }),
  }));

  let tuiles: JSX.Element;
  let nonComptees = 0;
  if (vue.vue === 'direction') {
    const m = courant as MoisComptable;
    const aSaisir = m.totaux.aSaisir + m.aClasser.aSaisir;
    const aClasser = m.aClasser.lignes;
    const aTraiter = aSaisir + aClasser;
    nonComptees = cloture ? 0 : aSaisir;
    tuiles = (
      <>
        <Tuile titre={t('comptabilite.tuiles.ht')} aide={t('comptabilite.tuiles.htAide')} cle>
          {f.euros(m.totaux.htCentimes)}
        </Tuile>
        <Tuile titre={t('comptabilite.tuiles.tva')} aide={t('comptabilite.tuiles.tvaAide')}>
          {f.euros(m.totaux.tvaCentimes)}
        </Tuile>
        <Tuile titre={t('comptabilite.tuiles.ttc')} aide={t('comptabilite.tuiles.ttcAide')}>
          {f.euros(m.totaux.ttcCentimes)}
        </Tuile>
        <Tuile
          titre={t('comptabilite.tuiles.aTraiter')}
          aide={
            cloture
              ? t('comptabilite.tuiles.aTraiterFige')
              : aTraiter > 0
                ? t('comptabilite.tuiles.aTraiterDetail', { saisir: aSaisir, classer: aClasser })
                : t('comptabilite.tuiles.aTraiterRien')
          }
          texte={aTraiter === 0}
        >
          {aTraiter > 0
            ? t('comptabilite.tuiles.lignes', { count: aTraiter })
            : t('comptabilite.tuiles.rien')}
        </Tuile>
      </>
    );
  } else {
    const m = courant as MoisComptableAssocie;
    const cumul = vue.mois
      .filter((x) => x.statut === 'cloture')
      .reduce((total, x) => total + (x as MoisComptableAssocie).monTotalCentimes, 0);
    const nbClotures = vue.mois.filter((x) => x.statut === 'cloture').length;
    tuiles = (
      <>
        <Tuile
          titre={t('comptabilite.tuilesAssocie.ht')}
          aide={t('comptabilite.tuilesAssocie.htAide')}
        >
          {f.euros(m.totalHtCentimes)}
        </Tuile>
        <Tuile
          titre={t('comptabilite.tuilesAssocie.maPart')}
          aide={t(
            cloture
              ? 'comptabilite.tuilesAssocie.maPartCloture'
              : 'comptabilite.tuilesAssocie.maPartProvisoire',
          )}
          cle
        >
          {f.euros(m.monTotalCentimes)}
        </Tuile>
        <Tuile
          titre={t('comptabilite.tuilesAssocie.cumul')}
          aide={t('comptabilite.tuilesAssocie.cumulAide', { count: nbClotures })}
        >
          {f.euros(cumul)}
        </Tuile>
        <Tuile
          titre={t('comptabilite.tuilesAssocie.statut')}
          aide={
            cloture
              ? t('comptabilite.tuilesAssocie.statutCloture')
              : t('comptabilite.tuilesAssocie.statutProvisoire')
          }
          texte
        >
          {t(`comptabilite.statut.${cloture ? 'cloture' : 'provisoire'}`)}
        </Tuile>
      </>
    );
  }

  return (
    <Section
      id="mois"
      numero={numero}
      titre={t('comptabilite.mois.titre')}
      pastille={<PastilleStatut cloture={cloture} />}
      actions={
        <div className={styles.moisNav} role="group" aria-label={t('comptabilite.mois.navigation')}>
          <Button
            variant="secondary"
            aria-label={t('comptabilite.mois.precedent')}
            disabled={position <= 0}
            onClick={() => onChoisir(liste[position - 1]!.mois)}
          >
            <ChevronLeft size={18} strokeWidth={1.5} aria-hidden="true" />
          </Button>
          <Select
            aria-label={t('comptabilite.mois.affiche')}
            options={options}
            value={courant.mois}
            onValueChange={onChoisir}
          />
          <Button
            variant="secondary"
            aria-label={t('comptabilite.mois.suivant')}
            disabled={position >= liste.length - 1}
            onClick={() => onChoisir(liste[position + 1]!.mois)}
          >
            <ChevronRight size={18} strokeWidth={1.5} aria-hidden="true" />
          </Button>
        </div>
      }
    >
      <dl className={styles.tuiles}>{tuiles}</dl>
      <NonComptees nombre={nonComptees} />
      <p className={commun.noteDiscrete}>
        {vue.vue === 'direction' ? t('comptabilite.noteTva') : t('comptabilite.noteHt')}
      </p>
    </Section>
  );
}
